import React, { useEffect, useRef } from 'react';
import core, { App } from '@gin-betting/core';
import cn from 'classnames';

import { BettingWidgets } from 'app-constants';
import { ErrorBoundary } from 'components';
import { useWidgetSettings } from 'hooks';
import type { OddButtonComponent } from 'types';
import { checkIsMatchTrackerDisabled } from 'utils';
import { StreamProps } from '../../types';
import DesktopStream from './DesktopStream';
import MobileStream from './MobileStream';
import useMatchTracker from './useMatchTracker';
import useStreamControls from './useStreamControls';
import useStreamPlayerBreakpoints from './useStreamPlayerBreakpoints';

import './stream.scss';

const {
    utils: { isOneOfPages, getDisplayName },
} = core;

interface Props {
    OddButton: OddButtonComponent;
    backButton?: JSX.Element | null;
    betradarId: string | null;
    hasMatchLog: boolean;
    isHeaderAtTop: boolean;
    matchId: string;
    sportId: string | undefined;
    title: string;
    toggleFavoriteButton?: JSX.Element | null;
    url: string | null;
    withPromoMatch: boolean;
}

const Stream: React.FC<Props> = ({
    OddButton,
    backButton,
    betradarId,
    hasMatchLog,
    isHeaderAtTop,
    matchId,
    sportId,
    title,
    toggleFavoriteButton,
    url,
    withPromoMatch,
}) => {
    const streamEl = useRef<HTMLDivElement>(null);
    const { current } = streamEl;

    const { scrollToFirstStream, disableMatchTracker } = useWidgetSettings({
        widget: BettingWidgets.STREAM,
    });

    const isMatchTrackerDisabled = checkIsMatchTrackerDisabled(
        disableMatchTracker,
        sportId
    );

    useEffect(() => {
        if (!current || !scrollToFirstStream) return;

        const timer = setTimeout(() => {
            current.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            });
        }, 200);

        return () => {
            clearTimeout(timer);
        };
    }, [current, scrollToFirstStream]);

    const {
        autoplay,
        initPlayer,
        isFullscreenActive,
        isSmall,
        isValidSportId,
        onChangeViewerModeToMatchTracker,
        onChangeViewerModeToStream,
        onStreamUnpin,
        onTogglePlayStatus,
        onToggleStreamFullscreen,
        player,
        viewerMode,
    } = useStreamControls({
        betradarId,
        hasMatchLog,
        isMatchTrackerDisabled,
        matchId,
        sportId,
        url,
    });

    const { styleProps, loadScriptStatus, initRef } = useMatchTracker({
        isFullscreenActive,
        isSmall,
        betradarId,
        viewerMode,
    });

    const isOnMatch = isOneOfPages([App.Routes.MATCH, App.Routes.RESULT_MATCH]);

    const {
        initRefForContainer,
        heightStreamContainer,
        widthStreamPlayer,
    } = useStreamPlayerBreakpoints();

    const streamProps: StreamProps = {
        OddButton,
        autoplay,
        backButton,
        betradarId,
        hasMatchLog,
        initPlayer,
        initRef,
        isFullscreenActive,
        isHeaderAtTop,
        isMatchTrackerDisabled,
        isOnMatch,
        isSmall,
        isValidSportId,
        loadScriptStatus,
        matchId,
        onChangeViewerModeToMatchTracker,
        onChangeViewerModeToStream,
        onStreamUnpin,
        onTogglePlayStatus,
        onToggleStreamFullscreen,
        player,
        styleProps,
        title,
        toggleFavoriteButton,
        url,
        viewerMode,
        widthStreamPlayer,
        withPromoMatch,
    };

    return (
        <div
            styleName={cn('container', {
                'is-fullscreen-active': isFullscreenActive,
            })}
            className="__app-Stream-container"
            style={{
                height:
                    !isFullscreenActive && !isSmall
                        ? heightStreamContainer
                        : '100%',
            }}
            ref={initRefForContainer}
        >
            {isSmall ? (
                <MobileStream {...streamProps} />
            ) : (
                <DesktopStream {...streamProps} />
            )}
        </div>
    );
};

const StreamWr: React.FC<Props> = props => {
    const name = getDisplayName(Stream);

    return (
        <ErrorBoundary wrappedComponentName={name}>
            <Stream {...props} />
        </ErrorBoundary>
    );
};

export default React.memo(StreamWr);
